import { fetchAllPages } from '@/lib/fetch-all-pages';
import { graphqlClient } from '@/lib/graphql-client';
import { assertMutationSaved, MutationResult, MutationSaveError } from '@/lib/mutation-result';
import {
  buildGetLeaveCategoriesQuery,
  buildGetLeaveRequestsOfCategoryQuery,
  buildGetTermAllotmentsForCategoryCheckQuery,
} from '../graphql/queries';
import {
  DELETE_LEAVE_CATEGORY_MUTATION,
  INSERT_LEAVE_CATEGORY_MUTATION,
  UPDATE_LEAVE_CATEGORY_MUTATION,
} from '../graphql/mutations';
import {
  countTermsUsingCategory,
  isLeaveCategoryTitleTaken,
  isSystemLeaveCategory,
} from '../utils/leave-category-rules';
import {
  GetLeaveCategoriesParams,
  LeaveCategoriesPagingResult,
  LeaveCategory,
  LeaveCategoryInsertInput,
  LeaveCategoryUpdateInput,
} from '../types/leave-category.types';

export const getLeaveCategories = async (
  params: GetLeaveCategoriesParams
): Promise<{ LeaveCategorys: LeaveCategoriesPagingResult }> => {
  const { pageNo, pageSize, filter = {}, order } = params;
  return graphqlClient.query({
    query: buildGetLeaveCategoriesQuery(filter, pageNo, pageSize, order),
  }) as Promise<{ LeaveCategorys: LeaveCategoriesPagingResult }>;
};

const CHECK_PAGE_SIZE = 500;

/** Every leave category of the organization (the data rules scope the read to it). */
const getAllLeaveCategories = () =>
  fetchAllPages(async (pageNo, pageSize) => {
    const result = await getLeaveCategories({
      pageNo,
      pageSize,
      order: [{ field: 'CreatedDate', direction: 'ASC' }],
    });
    return result.LeaveCategorys;
  }, CHECK_PAGE_SIZE);

/** Refuses a title another category of the organization already has (trimmed, case-insensitive). */
const assertTitleAvailable = async (title: string, exceptId?: string): Promise<LeaveCategory[]> => {
  const all = await getAllLeaveCategories();
  if (isLeaveCategoryTitleTaken(title, all, exceptId)) {
    throw new MutationSaveError('LEAVE_TYPE_TITLE_TAKEN');
  }
  return all;
};

export const createLeaveCategory = async (params: LeaveCategoryInsertInput) => {
  const input = { ...params, Title: params.Title.trim() };
  await assertTitleAvailable(input.Title);
  const result = (await graphqlClient.mutate({
    query: INSERT_LEAVE_CATEGORY_MUTATION,
    variables: { input },
  })) as { insertLeaveCategory?: MutationResult };
  assertMutationSaved(result.insertLeaveCategory, true, 'LEAVE_TYPE_SAVE_FAILED');
  return result;
};

export const updateLeaveCategory = async (params: {
  itemId: string;
  input: LeaveCategoryUpdateInput;
}) => {
  const title = params.input.Title.trim();
  const all = await assertTitleAvailable(title, params.itemId);
  // The workflows find "Sick Leave" and "Overtime Leave" by title: they keep it.
  const current = all.find((category) => category.ItemId === params.itemId);
  if (current && isSystemLeaveCategory(current.Title) && current.Title.trim() !== title) {
    throw new MutationSaveError('LEAVE_TYPE_SYSTEM_LOCKED');
  }
  const input = { ...params.input, Title: title };
  const filter = JSON.stringify({ _id: params.itemId });
  const result = (await graphqlClient.mutate({
    query: UPDATE_LEAVE_CATEGORY_MUTATION,
    variables: { filter, input },
  })) as { updateLeaveCategory?: MutationResult };
  assertMutationSaved(result.updateLeaveCategory, false, 'LEAVE_TYPE_SAVE_FAILED');
  return result;
};

/**
 * Deletes a leave category, unless the workflows look it up by title, an HR term still lists it among its
 * leave allotments, or a leave request is of it (the requests and the leave sheet would lose their
 * category). These reads see what the caller's data rules let them read, so the check is complete only for
 * holders of all-department read access; today only super_admin / workflow-operator hold
 * `leave-category::delete`, so no workflow is needed for it.
 */
export const deleteLeaveCategory = async (category: Pick<LeaveCategory, 'ItemId' | 'Title'>) => {
  if (isSystemLeaveCategory(category.Title))
    throw new MutationSaveError('LEAVE_TYPE_SYSTEM_LOCKED');
  const terms = await fetchAllPages(async (pageNo, pageSize) => {
    const result = await graphqlClient.query<{
      HrProfiles: {
        totalCount: number;
        items: { LeaveAllotments?: { LeaveCategoryId?: string }[] | null }[];
      };
    }>({ query: buildGetTermAllotmentsForCategoryCheckQuery(pageNo, pageSize) });
    return result.HrProfiles;
  }, CHECK_PAGE_SIZE);
  if (countTermsUsingCategory(terms, category.ItemId) > 0) {
    throw new MutationSaveError('LEAVE_TYPE_IN_USE');
  }
  const requests = await graphqlClient.query<{ LeaveRequests: { totalCount: number } }>({
    query: buildGetLeaveRequestsOfCategoryQuery(category.ItemId),
  });
  if (requests.LeaveRequests.totalCount > 0) throw new MutationSaveError('LEAVE_TYPE_IN_USE');
  const itemId = category.ItemId;
  const filter = JSON.stringify({ _id: itemId });
  const result = (await graphqlClient.mutate({
    query: DELETE_LEAVE_CATEGORY_MUTATION,
    variables: { filter, input: { isHardDelete: false } },
  })) as { deleteLeaveCategory?: MutationResult };
  assertMutationSaved(result.deleteLeaveCategory, false, 'LEAVE_TYPE_SAVE_FAILED');
  return result;
};
